import { Component, DestroyRef, inject, input } from '@angular/core';
import { NotificationLevel, SimpelNotification } from '@app/core/models/app-notification';
import { AppNotificationService } from '@app/core/services/custom-services/notification.service';
import { Align, PopupComponent } from '@progress/kendo-angular-popup';
import { bellIcon, SVGIcon, trashIcon, checkIcon, gridIcon, xIcon, checkCircleIcon } from '@progress/kendo-svg-icons';
import { interval } from 'rxjs';
import { NotificationQueryService } from '@app/core/services/http-services/app/notification-query.service';
import { NotificationDTO } from '@app/core/models/notification-dto';
import { NotificationSeveritySummaryService } from '@app/core/services/http-services/app/notification-severity-summary.service';
import { NotificationService } from '@app/core/services/http-services/app/notification.service';
import { State } from '@progress/kendo-data-query';
import { Router } from '@angular/router';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { NotificationSeverityIconComponent } from '../notification-severity-icon/notification-severity-icon.component';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { AsyncPipe } from '@angular/common';
import { NotificationItemComponent } from '@app-module/app-controls/notification-item/notification-item.component';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-notification-popup',
    templateUrl: './notification-popup.component.html',
    styleUrl: './notification-popup.component.css',
    imports: [ButtonComponent, LoaderComponent, NotificationSeverityIconComponent, SVGIconComponent, PopupComponent, NotificationItemComponent, AsyncPipe, TranslatePipe]
})
export class NotificationPopupComponent {
  showPopup = false;
  notifications: SimpelNotification[] = [];
  sessionNotifications: NotificationDTO[] = [];
  isLoadingSeveritySummary = false;
  isLoadingNotificationsPersisted = false;

  anchorAlign: Align = { horizontal: "right", vertical: "bottom" };
  popupAlign: Align = { horizontal: "right", vertical: "top" };
  margin = { horizontal: 0, vertical: 5 };

  //Icons
  bellIcon: SVGIcon = bellIcon;
  trashIcon: SVGIcon = trashIcon;
  checkIcon: SVGIcon = checkIcon;
  gridIcon: SVGIcon = gridIcon;
  xIcon: SVGIcon = xIcon;
  checkCircleIcon: SVGIcon = checkCircleIcon;

  notificationsPersisted: NotificationQueryService;
  severitySummary: NotificationSeveritySummaryService;

    public state: State = {
      skip: 0,
      take: 20,
      sort: []
    };

    public disabled = input<boolean>(false);

    private readonly destroyRef = inject(DestroyRef);
    public readonly notificationService = inject(AppNotificationService);
    private readonly notificationQueryService = inject(NotificationQueryService);
    private readonly notificationSeveritySummaryService = inject(NotificationSeveritySummaryService);
    private readonly notificationHttpService = inject(NotificationService);
    private readonly router = inject(Router);

  constructor() {
    this.notificationsPersisted = this.notificationQueryService;
    this.severitySummary = this.notificationSeveritySummaryService;
  }

  ngOnInit(): void {
    this.querySeveritySummary();

    // Query severity summary every 2 minutes
    interval(120000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.querySeveritySummary();
    });
  }

  togglePopup(): void {
    this.showPopup = !this.showPopup;

    if (this.showPopup) {
      // Load notifications when popup opens
      this.notificationService.notificationList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
        this.notifications = list;
        this.sessionNotifications = this.convertToNotificationDTO(list);
      });
      this.queryNotificationsPersisted();
    }
  }

  clearAll(): void {
    this.notificationService.clearNotifications();
    this.sessionNotifications = [];
    this.showPopup = false;
  }

  removeNotification(notification: NotificationDTO): void {
    // Find and remove from original list
    const index = this.sessionNotifications.findIndex(n => n.notificationId === notification.notificationId);
    if (index !== -1) {
      this.notifications.splice(index, 1);
      this.sessionNotifications.splice(index, 1);
    }
    if (this.sessionNotifications.length === 0)
      this.clearAll();
  }

  private convertToNotificationDTO(notifications: SimpelNotification[]): NotificationDTO[] {
    return notifications.map((n, index) => ({
      notificationId: `session-${index}`,
      title: n.level === NotificationLevel.WARNING ? 'Warning' : 'Error',
      text: n.message,
      severity: n.level === NotificationLevel.WARNING ? 1 : 2,
      type: 0,
      audience: 0,
      audienceUserId: null,
      receiverUserId: null,
      siteId: '',
      eventDate: new Date().toISOString(),
      reportDate: new Date().toISOString(),
      signedDate: null,
      signedByUserId: null,
      hasTranslation: false,
      translationData: null,
      docRefGuid: null,
      docRefInt: null,
      validTo: null
    }));
  }

  onNotificationSigned(): void {
    this.queryNotificationsPersisted();
    this.querySeveritySummary();
  }

  navigateToGrid(): void {
    this.showPopup = false;
    this.router.navigate(['/notifications']);
  }

  sign(notification: NotificationDTO): void {
    this.notificationHttpService.sign(notification.notificationId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.onNotificationSigned();
      },
      error: (err) => {
        console.error('Failed to sign notification', err);
      }
    });
  }

  signAllNotifications(): void {
   this.notificationHttpService.signAll().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
     next: () => {
          this.onNotificationSigned();
     },
     error: (err) => {
         console.error('Failed to sign all notifications', err);
     },
   });
 }

  private async querySeveritySummary(): Promise<void> {
    this.isLoadingSeveritySummary = true;
    try {
    await this.notificationSeveritySummaryService.query();
    } catch (err) {
      
    } finally {
      this.isLoadingSeveritySummary = false;
    }
  }

  private async queryNotificationsPersisted(): Promise<void> {
    this.isLoadingNotificationsPersisted = true;
    await this.notificationQueryService.query(this.state);

    this.isLoadingNotificationsPersisted = false;
  }
}
